import { useState, useEffect } from 'react';
import { useSearchParams, Link, useLocation } from 'react-router-dom';
import { useSearchProducts } from '../api';
import { ProductGrid } from '../components/product/ProductGrid';
import { ProductFilters } from '../components/product/ProductFilters';
import { useAnalytics } from '../hooks/useAnalytics';
import { useCustomerIntelligence } from '../contexts/CustomerIntelligenceContext';
import { SEO, Breadcrumb } from '../components/SEO';

export function SearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const query = searchParams.get('q') ?? '';
  const page = parseInt(searchParams.get('page') ?? '1', 10);

  const [selectedBrand, setSelectedBrand] = useState<string | undefined>();
  const [selectedPriceRange, setSelectedPriceRange] = useState<string | undefined>();
  const [sortBy, setSortBy] = useState('relevance');
  const { trackSearch } = useAnalytics();
  const { trackSearch: trackSearchIntelligence } = useCustomerIntelligence();

  const { data, isLoading } = useSearchProducts(query, {
    brand: selectedBrand,
    // Additional filters would be applied here
  });

  // Track search and reset filters when query changes
  useEffect(() => {
    setSelectedBrand(undefined);
    setSelectedPriceRange(undefined);
    if (query) {
      trackSearch(query);
    }
  }, [query, trackSearch]);

  // Track search for AI personalization when results are loaded
  useEffect(() => {
    if (query && data) {
      trackSearchIntelligence(query, data.total);
    }
  }, [query, data, trackSearchIntelligence]);

  const handlePageChange = (newPage: number) => {
    searchParams.set('page', String(newPage));
    setSearchParams(searchParams);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearFilters = () => {
    setSelectedBrand(undefined);
    setSelectedPriceRange(undefined);
  };

  if (!query) {
    return (
      <div className="container py-12 text-center">
        <SearchIcon className="w-16 h-16 mx-auto text-gray-300" />
        <h1 className="mt-4 text-2xl font-bold text-gray-900">
          Busca productos
        </h1>
        <p className="mt-2 text-gray-600">
          Ingresa un termino de busqueda para encontrar productos
        </p>
        <Link to="/" className="mt-4 btn btn-primary inline-block">
          Explorar categorias
        </Link>
      </div>
    );
  }

  // Build canonical URL
  const canonicalUrl = typeof window !== 'undefined'
    ? `${window.location.origin}${location.pathname}${location.search}`
    : undefined;

  // Build breadcrumb items
  const breadcrumbItems = [
    { name: 'Inicio', url: typeof window !== 'undefined' ? window.location.origin : '/' },
    { name: `Busqueda: ${query}`, url: canonicalUrl ?? location.pathname },
  ];

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* SEO - noindex search results to avoid duplicate content */}
      <SEO
        title={`Resultados para "${query}"`}
        description={`Encuentra ${query} en PetMas. ${data?.total ?? 0} productos disponibles.`}
        canonical={canonicalUrl}
        noIndex
      />

      {/* Header */}
      <div className="bg-white border-b">
        <div className="container py-8">
          {/* Breadcrumb */}
          <div className="mb-4">
            <Breadcrumb items={breadcrumbItems} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Resultados para "{query}"
          </h1>
          {data && (
            <p className="mt-1 text-gray-600">
              {data.total} {data.total === 1 ? 'producto encontrado' : 'productos encontrados'}
            </p>
          )}
        </div>
      </div>

      <div className="container py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar filters */}
          <aside className="lg:w-64 flex-shrink-0">
            <ProductFilters
              brands={[]}
              priceRanges={[
                { value: '0-50', label: 'Hasta S/50' },
                { value: '50-100', label: 'S/50 - S/100' },
                { value: '100-200', label: 'S/100 - S/200' },
                { value: '200+', label: 'Mas de S/200' },
              ]}
              selectedBrand={selectedBrand}
              selectedPriceRange={selectedPriceRange}
              onBrandChange={setSelectedBrand}
              onPriceRangeChange={setSelectedPriceRange}
              onClearAll={handleClearFilters}
            />
          </aside>

          {/* Main content */}
          <main className="flex-1">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                {/* Sort */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="input py-1.5 px-3 w-auto"
                >
                  <option value="relevance">Relevancia</option>
                  <option value="price-asc">Precio: menor a mayor</option>
                  <option value="price-desc">Precio: mayor a menor</option>
                  <option value="name-asc">Nombre: A-Z</option>
                </select>
              </div>
            </div>

            {/* Results */}
            {isLoading ? (
              <ProductGrid products={[]} isLoading />
            ) : data && data.items.length > 0 ? (
              <>
                <ProductGrid products={data.items} />

                {/* Pagination */}
                {data.total > data.pageSize && (
                  <Pagination
                    currentPage={page}
                    totalPages={Math.ceil(data.total / data.pageSize)}
                    onPageChange={handlePageChange}
                  />
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <EmptySearchIcon className="w-16 h-16 mx-auto text-gray-300" />
                <h2 className="mt-4 text-xl font-semibold text-gray-900">
                  No encontramos resultados
                </h2>
                <p className="mt-2 text-gray-600">
                  Intenta con otras palabras clave o explora nuestras categorias
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-2">
                  <Link to="/perros" className="btn btn-secondary">
                    Perros
                  </Link>
                  <Link to="/gatos" className="btn btn-secondary">
                    Gatos
                  </Link>
                  <Link to="/outlet" className="btn btn-secondary">
                    Outlet
                  </Link>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Search suggestions */}
      {data && data.items.length === 0 && (
        <section className="py-12 bg-white">
          <div className="container">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Sugerencias de busqueda
            </h2>
            <ul className="space-y-2 text-gray-600">
              <li>• Verifica que las palabras esten escritas correctamente</li>
              <li>• Intenta con terminos mas generales</li>
              <li>• Usa menos palabras en tu busqueda</li>
              <li>• Prueba con sinonimos o terminos relacionados</li>
            </ul>
          </div>
        </section>
      )}
    </div>
  );
}

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps) {
  const pages: (number | 'ellipsis')[] = [];

  for (let i = 1; i <= totalPages; i++) {
    if (
      i === 1 ||
      i === totalPages ||
      (i >= currentPage - 1 && i <= currentPage + 1)
    ) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== 'ellipsis') {
      pages.push('ellipsis');
    }
  }

  return (
    <nav className="flex items-center justify-center gap-1 mt-8">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronLeftIcon className="w-5 h-5" />
      </button>

      {pages.map((page, index) =>
        page === 'ellipsis' ? (
          <span key={`ellipsis-${index}`} className="px-2 text-gray-400">
            ...
          </span>
        ) : (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`px-3 py-1 rounded ${
              currentPage === page
                ? 'bg-primary text-white'
                : 'hover:bg-gray-100'
            }`}
          >
            {page}
          </button>
        )
      )}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronRightIcon className="w-5 h-5" />
      </button>
    </nav>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}

function EmptySearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}
